import { DocumentNode } from 'graphql'
import React, { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import papaparse from 'papaparse'
import { t, Trans } from '@lingui/macro'
import { get } from 'lodash'

import {
  ButtonOutline,
  ExtendedChakraButtonProps
} from 'front-end/src/components'
import { notification } from '../notifications/Notifications'
import { downloadAsFile } from '../../../utils/downloadAsFile'

interface IDownloadCSVButtonProps
  extends Omit<ExtendedChakraButtonProps, 'children'> {
  className?: string
  count?: number
  fileName: string
  dataPath: string
  title?: string
  ButtonComponent?: React.FC<React.ComponentProps<typeof ButtonOutline>>
  queryOptions: (
    page: number,
    limit: number
  ) => {
    variables: Record<string, any>
    query: DocumentNode
  }
  prepareData: (data) => Array<Record<string, unknown>>
  customBatchSize?: number
  onFetchStart?: () => void
  onFetchEnd?: () => void
  disabled?: boolean
}

export const DEFAULT_BATCH_SIZE = 500

export const DownloadCSVButton = ({
  className,
  ButtonComponent = ButtonOutline,
  count,
  fileName,
  dataPath,
  queryOptions,
  prepareData,
  title,
  customBatchSize,
  onFetchStart,
  onFetchEnd,
  disabled,
  ...rest
}: IDownloadCSVButtonProps) => {
  const batchSize =
    customBatchSize && customBatchSize > 0
      ? customBatchSize
      : DEFAULT_BATCH_SIZE

  const client = useApolloClient()
  const [fetching, setFetching] = useState<boolean>(false)
  const [percentDone, setPercentDone] = useState<number | null>(0)

  const isInProgress = fetching || !!(percentDone && percentDone > 0)
  const isDisabled = disabled || isInProgress

  return (
    <ButtonComponent
      className={className}
      progressPercents={percentDone}
      disabled={isDisabled}
      onClick={async () => {
        let data

        const pages = Math.ceil((count || 1) / batchSize) || 1
        data = []
        setFetching(true)
        onFetchStart?.()

        try {
          for (let page = 1; page <= pages; page++) {
            const response = await client.query(queryOptions(page, batchSize))

            const newPercent = page * (100 / pages)
            setPercentDone(newPercent)

            // When all fetching is finished, return percent to default state after 500ms
            if (newPercent === 100) {
              setTimeout(() => {
                setPercentDone(null)
              }, 500)
            }

            data = data.concat(get(response, dataPath))
          }
          const csv = papaparse.unparse(prepareData(data))
          downloadAsFile(csv, fileName)
        } catch (err) {
          notification.error(t`CSV download failed`)
          throw err
        } finally {
          setFetching(false)
          onFetchEnd?.()
        }
      }}
      dataTest="DownloadCSVButton"
      {...rest}
    >
      {isInProgress ? (
        <Trans>Loading...</Trans>
      ) : title ? (
        title
      ) : (
        <Trans>Export data (.csv)</Trans>
      )}
    </ButtonComponent>
  )
}
